import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Button from "../../components/shared/Button/Button";
import Spinner from "../../components/shared/spinner/Spinner";
import { HiChevronLeft } from "react-icons/hi";
import { useAllSwingCollectionsByUserId } from "../swingCollections/hooks/useAllSwingCollectionsByUserId";
import { useSwingCollection } from "../swingCollections/hooks/useSwingCollection";
import CollectionSelectorCard from "./components/CollectionSelectorCard";
import SummaryAI from "../swingCollections/Collection/components/SummaryAI";
import Divider from "../../components/shared/Divider/Divider";
import Top5Outliers from "./components/Top5Outliers";
import AllIndicators from "./components/AllIndicators/AllIndicators";
import { COLLECTIONS_JOIN_SYMBOL_URL } from "../swingCollections/constants/collectionsJoinSymbolUrl";
import Newton from "../swing_library/components/Newton/Newton";

export default function Compare() {
  const history = useHistory();
  const location = useLocation();
  const { collections = [] } = useAllSwingCollectionsByUserId();

  // Check if we have temporary collections from quick compare
  const tempCollections = location.state?.tempCollections;

  const collectionIds = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const idsParam = params.get("ids");
    if (!idsParam) return [];
    return idsParam.split(COLLECTIONS_JOIN_SYMBOL_URL).filter(Boolean);
  }, [location.search]);

  const [selectedCollectionId1, setSelectedCollectionId1] = useState(
    tempCollections ? tempCollections[0]?.id : collectionIds[0] ?? null
  );
  const [selectedCollectionId2, setSelectedCollectionId2] = useState(
    tempCollections ? tempCollections[1]?.id : collectionIds[1] ?? null
  );

  useEffect(() => {
    setSelectedCollectionId1(collectionIds[0] ?? null);
    setSelectedCollectionId2(collectionIds[1] ?? null);
  }, [collectionIds]);

  // Use temp collections if available, otherwise fetch from API
  const { collection: apiCollection1, isLoading: isLoading1 } =
    useSwingCollection(
      !tempCollections && selectedCollectionId1
        ? selectedCollectionId1
        : undefined
    );
  const { collection: apiCollection2, isLoading: isLoading2 } =
    useSwingCollection(
      !tempCollections && selectedCollectionId2
        ? selectedCollectionId2
        : undefined
    );

  // Use temp collections or API collections
  const collection1 = tempCollections ? tempCollections[0] : apiCollection1;
  const collection2 = tempCollections ? tempCollections[1] : apiCollection2;

  const isLoading = tempCollections ? false : isLoading1 || isLoading2;

  const availableCollections1 = useMemo(
    () =>
      tempCollections
        ? []
        : collections.filter((c) => c.id !== selectedCollectionId2),
    [collections, selectedCollectionId2, tempCollections]
  );

  const availableCollections2 = useMemo(
    () =>
      tempCollections
        ? []
        : collections.filter((c) => c.id !== selectedCollectionId1),
    [collections, selectedCollectionId1, tempCollections]
  );

  const updateUrl = useCallback(
    (id1, id2) => {
      const ids = [id1, id2].filter(Boolean);
      const path = ids.length
        ? `/compare?ids=${ids.join(COLLECTIONS_JOIN_SYMBOL_URL)}`
        : "/compare";
      history.push(path);
    },
    [history]
  );

  const handleCollection1Change = useCallback(
    (collectionId) => {
      setSelectedCollectionId1(collectionId);
      updateUrl(collectionId, selectedCollectionId2);
    },
    [selectedCollectionId2, updateUrl]
  );

  const handleCollection2Change = useCallback(
    (collectionId) => {
      setSelectedCollectionId2(collectionId);
      updateUrl(selectedCollectionId1, collectionId);
    },
    [selectedCollectionId1, updateUrl]
  );

  const handleRemoveCollection1 = useCallback(() => {
    setSelectedCollectionId1(null);
    updateUrl(null, selectedCollectionId2);
  }, [selectedCollectionId2, updateUrl]);

  const handleRemoveCollection2 = useCallback(() => {
    setSelectedCollectionId2(null);
    updateUrl(selectedCollectionId1, null);
  }, [selectedCollectionId1, updateUrl]);

  const isCompareActive = Boolean(
    selectedCollectionId1 && selectedCollectionId2
  );

  if (isLoading) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <Spinner color="primary500" size="md" />
      </div>
    );
  }

  return (
    <>
      <div className="flex w-full flex-col gap-8 py-5">
        <div className="flex flex-col gap-8 px-5">
        <div className="flex flex-col gap-1">
          <Button
            color="gray100"
            isIconOnly
            onClick={() => history.push("/collections")}
            rounded="full"
            size="xs"
          >
            <HiChevronLeft className="text-gray700" />
          </Button>

          <div className="flex items-center justify-between gap-4">
            <h1 className="text-gray900 text-2xl font-semibold">Compare</h1>

            {/* {isCompareActive && (
              <Button
                color="primary500"
                onClick={() => console.log("Download Report")}
              >
                Download Report
              </Button>
            )} */}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6">
          <CollectionSelectorCard
            cardColor="blue"
            collection={collection1}
            collections={availableCollections1}
            onRemove={handleRemoveCollection1}
            onSelect={handleCollection1Change}
            isQuickCompare={!!tempCollections}
          />

          <CollectionSelectorCard
            cardColor="pink"
            collection={collection2}
            collections={availableCollections2}
            onRemove={handleRemoveCollection2}
            onSelect={handleCollection2Change}
            isQuickCompare={!!tempCollections}
          />
        </div>

        <Divider color="gray200" type="horizontal" />

        {isCompareActive && (
          <SummaryAI collectionOne={collection1} collectionTwo={collection2} />
        )}
      </div>

      <div className="pl-5">
        <Top5Outliers collection1={collection1} collection2={collection2} />
      </div>

      <div className="px-5">
        <AllIndicators collection1={collection1} collection2={collection2} />
      </div>
      </div>

      <Newton />
    </>
  );
}
